import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { TrendingUp, AlertTriangle, ExternalLink } from 'lucide-react';

export function CreateBetSummary({
    hash,
    formData,
    selectedCurrency,
    oddsDisplay,
    takerStake,
    totalPool,
    isPending,
    isConfirming,
    isConnected,
    hasEnoughBalance,
    buttonContent,
}) {
    const { t } = useTranslation();
    return (
        <Card className="bg-white/[0.02] border-white/5 sticky top-24">
            <CardHeader>
                <CardTitle className="text-xl text-white">Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {hash && (
                    <div className="p-3 rounded-lg bg-[#00FF66]/10 border border-[#00FF66]/20">
                        <div className="text-xs text-[#00FF66] mb-1">Transaction Hash</div>
                        <a href={`https://basescan.org/tx/${hash}`} target="_blank" rel="noopener noreferrer"
                            className="font-mono text-xs text-white hover:text-[#00FF66] flex items-center gap-1">
                            {hash.slice(0, 10)}...{hash.slice(-8)}
                            <ExternalLink className="w-3 h-3" />
                        </a>
                    </div>
                )}

                <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 space-y-3">
                    <div className="flex justify-between items-center">
                        <span className="text-white/50">{t('createBet.yourStake')}</span>
                        <span className="font-mono text-white" data-testid="summary-stake">
                            {formData.stake || '0'} {selectedCurrency.code}
                        </span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-white/50">{t('createBet.odds')}</span>
                        <span className="font-mono text-[#00FF66] font-bold">{oddsDisplay}x</span>
                    </div>
                    <div className="border-t border-white/10 pt-3">
                        <div className="flex justify-between items-center">
                            <span className="text-white/50">{t('createBet.takerStake')}</span>
                            <span className="font-mono text-[#FFD700]">
                                {takerStake} {selectedCurrency.code}
                            </span>
                        </div>
                    </div>
                    <div className="border-t border-white/10 pt-3">
                        <div className="flex justify-between items-center">
                            <span className="text-white/50">{t('createBet.totalPool')}</span>
                            <span className="font-mono text-lg text-[#00FF66] font-bold" data-testid="summary-total-pool">
                                {totalPool} {selectedCurrency.code}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-white/50">{t('createBet.ifYouWin')}</h4>
                    <div className="p-3 rounded-lg bg-[#00FF66]/10 border border-[#00FF66]/20">
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-[#00FF66]" />
                            <span className="font-mono text-[#00FF66] font-bold">
                                +{takerStake} {selectedCurrency.code}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-white/50">{t('createBet.ifYouLose')}</h4>
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-400" />
                            <span className="font-mono text-red-400 font-bold">
                                -{formData.stake || '0'} {selectedCurrency.code}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5 text-xs text-white/40" data-testid="summary-fee-notice">
                    <p>{t('currency.platformFeeNotice', { pct: selectedCurrency.platform_fee_pct })}</p>
                    {selectedCurrency.reward_multiplier > 1 && (
                        <p className="text-[#FFD800] mt-1">{t('currency.bonusRewardsLine', { mult: selectedCurrency.reward_multiplier })}</p>
                    )}
                </div>

                <Button
                    type="submit"
                    className="w-full bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52] hover:shadow-[0_0_20px_rgba(130,255,122,0.4)] font-semibold py-6 text-lg disabled:opacity-50"
                    disabled={isPending || isConfirming || !formData.description || !formData.stake || (isConnected && !hasEnoughBalance)}
                    data-testid="submit-bet-button"
                >
                    {buttonContent}
                </Button>

                <div className="text-center text-xs text-white/30">Contract: 0x4871...D6E2 on Base</div>
            </CardContent>
        </Card>
    );
}
